// Le versement — la traduction d'un tampon d'appareil en travail d'étape.
//
// Tout le calcul est fait par le serveur (`fc_hw_apercu`) : ce fichier ne
// contient que ce dont l'écran a besoin pour PARLER de ce qu'il montre. Porter
// le recoupement ici en ferait une seconde vérité, qui dériverait de celle du
// serveur au premier changement de convention — et c'est justement ce genre
// d'écart qui a rendu deux verdicts silencieusement morts (migration 15).

/** Les quatre modules que l'appareil sait capturer. */
export type ModuleCapture = 'depouillement' | 'retours' | 'formation' | 'coffre'

/** Le sort d'une ligne au versement. */
export type Verdict = 'a_verser' | 'deja' | 'absent' | 'info'

export interface LigneApercu {
  id: string
  geste: string
  titre: string
  sous: string
  verdict: Verdict
  montant_cents?: number | null
  qte?: number
  motif?: string
  reference?: string
  paiement?: string | null
}

export interface Apercu {
  session: string
  module: ModuleCapture
  statut: 'ouverte' | 'livree' | 'versee' | 'annulee'
  etape: string | null
  n: number
  ouverte_par: string | null
  livree_par: string | null
  livree_at: string | null
  /** Non nul = le versement est impossible, et pour cette raison-là. */
  bloquant: string | null
  lignes: LigneApercu[]
  cible: Record<string, unknown>
  resume: Partial<Record<Verdict, number>> | null
}

export interface SessionEnAttente {
  id: string
  module: ModuleCapture
  etape: string | null
  n: number
  livree_at: string | null
  livree_par: string | null
}

/**
 * À quelle étape un module rend ses comptes.
 *
 * Le rattachement se fait par le MODULE et non par `etape_armee` : l'appareil
 * peut ouvrir un tampon sans étape armée (il le fait quand on entre dans un
 * module depuis l'accueil), et un tampon sans étape n'apparaîtrait alors nulle
 * part. Le module, lui, est toujours là — c'est une contrainte de la table.
 */
export const ETAPE_DU_MODULE: Record<ModuleCapture, string> = {
  depouillement: '05B',
  retours: '07',
  formation: '08',
  coffre: '10',
}

export const LIBELLE_MODULE: Record<ModuleCapture, string> = {
  depouillement: 'Dépouillement',
  retours: 'Retours',
  formation: 'Formation',
  coffre: 'Coffre',
}

export const modulesDeLEtape = (code: string): ModuleCapture[] =>
  (Object.keys(ETAPE_DU_MODULE) as ModuleCapture[]).filter((m) => ETAPE_DU_MODULE[m] === code)

/**
 * D14 s'applique ici comme ailleurs : la couleur dit la NATURE, jamais le
 * confort. `absent` est ambre et non rouge — un ticket introuvable n'est pas
 * une faute, c'est une chose à regarder.
 */
export const COULEUR_VERDICT: Record<Verdict, 'ok' | 'warn' | 'neutre' | 'act'> = {
  a_verser: 'act',
  deja: 'neutre',
  absent: 'warn',
  info: 'neutre',
}

export const LIBELLE_VERDICT: Record<Verdict, string> = {
  a_verser: 'À VERSER',
  deja: 'DÉJÀ FAIT',
  absent: 'INTROUVABLE',
  info: 'CONTRÔLE',
}

/** Ce qu'on écrira réellement — décoché compris. */
export function compteAVerser(lignes: LigneApercu[], ignores: Set<string>): number {
  return lignes.filter((l) => l.verdict === 'a_verser' && !ignores.has(l.id)).length
}

/**
 * La phrase du bouton. Elle annonce un NOMBRE d'écritures, pas un « valider » :
 * on doit savoir combien de choses vont bouger avant de le presser.
 */
export function phraseVersement(n: number, module: ModuleCapture): string {
  if (n === 0) return 'Rien à verser'
  const quoi = module === 'coffre' ? 'entrée' : module === 'depouillement' ? 'vérification' : 'ticket'
  return `Verser ${n} ${quoi}${n > 1 ? 's' : ''}`
}
